import {
  VetFormLinkData,
  VeterinarioForm,
} from '../../types/Reintegro/Veterinario/types';
import { SVF } from '../../types/Siniestro/types';
import { normalize } from '../../utils/string';
import { getVetFormIdFromSVFs } from '../../utils/vetFormLink';
import { reSendFields, sendFields } from '../Helpers/useSiniestroPage';
import { usePostVetFormLink, useReSendVetFormLink } from './useVeterinario';

type Params = {
  currentSiniestroId: string;
  vetFormIdRef: React.MutableRefObject<number | null>;
  submittedVetValues: React.MutableRefObject<VeterinarioForm | null>;
  onNext: () => void;
  showAlert: (opts: { header: string; message: string; buttons: any[] }) => void;
  mutateSVFs?: () => Promise<SVF[] | undefined>;
};

export const useVetFormSend = ({
  currentSiniestroId,
  vetFormIdRef,
  submittedVetValues,
  onNext,
  showAlert,
  mutateSVFs,
}: Params) => {
  const { postVetFormLink, isLoading: isPostLoading } = usePostVetFormLink();
  const { reSendVetFormLink, isLoading: isReSendLoading } = useReSendVetFormLink();

  const isLoading = isPostLoading || isReSendLoading;

  /* ───────────────────────────────
   * RE-SEND DIRECTO DESDE SVF
   * ─────────────────────────────── */
  const resendFromSVF = async (
    values: VeterinarioForm,
    body: VetFormLinkData,
    vetFormId: number,
    skipMutate?: boolean,
  ) => {
    console.log('[VetFormSend] resendFromSVF start', {
      currentSiniestroId,
      vetFormId,
      skipMutate,
      values,
      body,
    });

    const res = await reSendVetFormLink({
      vetFormLinkData: body,
      subVetFormId: vetFormId,
    });

    console.log('[VetFormSend] resendFromSVF response', res);

    if (!res.ok) {
      showAlert({
        header: 'Error',
        message: 'No se pudo reenviar el link al veterinario.',
        buttons: [{ text: 'Entendido', role: 'cancel' }],
      });
      return false;
    }

    submittedVetValues.current = values;

    if (!skipMutate && mutateSVFs) {
      const svfs = await mutateSVFs();
      console.log('[VetFormSend] resendFromSVF mutateSVFs', svfs);
    }

    return true;
  };

  /* ───────────────────────────────
   * SUBMIT DESDE FORM
   * ─────────────────────────────── */
  const submit = async (values: VeterinarioForm, body: VetFormLinkData) => {
    const prev = submittedVetValues.current;

    console.log('[VetFormSend] submit start', {
      currentSiniestroId,
      vetFormIdRef: vetFormIdRef.current,
      prev,
      values,
      body,
    });

    // 🟢 PRIMER ENVÍO
    if (!prev) {
      console.log('[VetFormSend] first send request', body);

      const res = await postVetFormLink(body);

      console.log('[VetFormSend] first send response', res);

      if (!res.ok) {
        showAlert({
          header: 'Error',
          message: 'Hubo un error al enviar los datos del veterinario.',
          buttons: [{ text: 'Entendido', role: 'cancel' }],
        });
        return;
      }

      submittedVetValues.current = values;

      if (mutateSVFs) {
        const svfs = await mutateSVFs();
        console.log('[VetFormSend] first send mutateSVFs', svfs);

        if (svfs) {
          const id = getVetFormIdFromSVFs(svfs, Number(currentSiniestroId));
          if (id) vetFormIdRef.current = id;

          console.log('[VetFormSend] first send resolved vetFormId', {
            resolvedVetFormId: id,
            vetFormIdRef: vetFormIdRef.current,
          });
        }
      }

      console.log('[VetFormSend] first send next step', {
        vetFormIdRef: vetFormIdRef.current,
      });

      onNext();
      return;
    }

    // COMPARACIÓN
    const reSendChanged = reSendFields.some(
      (f) => normalize(values[f]) !== normalize(prev[f]),
    );

    const sendChanged = sendFields.some(
      (f) => normalize(values[f]) !== normalize(prev[f]),
    );

    console.log('[VetFormSend] change detection', {
      reSendChanged,
      sendChanged,
      vetFormIdRef: vetFormIdRef.current,
    });

    // AMBOS CAMBIARON → SEND NUEVO (nuevo link)
    if (reSendChanged && sendChanged) {
      console.log('[VetFormSend] resend and send fields changed request', body);

      const res = await postVetFormLink(body);

      console.log('[VetFormSend] resend and send fields changed response', res);

      if (!res.ok) {
        showAlert({
          header: 'Error',
          message: 'Hubo un error al enviar los datos del veterinario.',
          buttons: [{ text: 'Entendido', role: 'cancel' }],
        });
        return;
      }

      submittedVetValues.current = values;

      if (mutateSVFs) {
        const svfs = await mutateSVFs();
        console.log('[VetFormSend] changed fields mutateSVFs', svfs);

        if (svfs) {
          const id = getVetFormIdFromSVFs(svfs, Number(currentSiniestroId));
          if (id) vetFormIdRef.current = id;

          console.log('[VetFormSend] changed fields resolved vetFormId', {
            resolvedVetFormId: id,
            vetFormIdRef: vetFormIdRef.current,
          });
        }
      }

      onNext();
      return;
    }

    // SOLO RE-SEND FIELDS CAMBIARON
    if (reSendChanged) {
      const ok = await resendFromSVF(values, body, vetFormIdRef.current!);
      if (ok) onNext();
      return;
    }

    // SOLO SEND FIELDS CAMBIARON
    if (sendChanged) {
      console.log('[VetFormSend] send fields changed request', body);

      const res = await postVetFormLink(body);

      console.log('[VetFormSend] send fields changed response', res);

      if (!res.ok) {
        showAlert({
          header: 'Error',
          message: 'Hubo un error al enviar los datos del veterinario.',
          buttons: [{ text: 'Entendido', role: 'cancel' }],
        });
        return;
      }

      submittedVetValues.current = values;

      if (mutateSVFs) {
        const svfs = await mutateSVFs();
        console.log('[VetFormSend] send fields changed mutateSVFs', svfs);
      }

      onNext();
      return;
    }

    // SIN CAMBIOS
    console.log('[VetFormSend] no changes, next step', {
      vetFormIdRef: vetFormIdRef.current,
    });

    onNext();
  };

  return {
    submit,
    resendFromSVF,
    isLoading,
  };
};
